import React from 'react';
import { Linking, View } from 'react-native';

import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import {
  Accent,
  BleedRule,
  CtaRow,
  DisplayHeading,
  GhostCta,
  Kicker,
  Lede,
  Marquee,
  PrimaryCta,
  Rule,
  useEntrance,
  useLanding,
} from '@/components/Landing/LandingKit';
import { EASE, LAYOUT, fluid, isWeb } from '@/components/Landing/landingTokens';
import { MicroLabel } from '@/components/Landing/MarketingKit';
import {
  CONTACT_EMAIL,
  ENGAGEMENT_SCOPE,
  HERO_MARQUEE,
} from '@/constants/enterpriseContent';

/** The masthead: what we do, who for, and the one action worth taking. */
function EnterpriseHero({ topInset = 0 }) {
  const { c, isXS, isCompact, width, pad, display } = useLanding();
  const entered = useEntrance();

  const headline = display({ min: 36, max: 76, leading: 0.96, tracking: -0.045 });

  return (
    <View
      accessibilityRole={isWeb ? 'banner' : undefined}
      accessibilityLabel="Gab AI Enterprise"
      style={{ width: '100%', alignSelf: 'stretch', alignItems: 'center' }}
    >
      {isWeb ? (
        <View
          aria-hidden
          pointerEvents="none"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 720,
            background: c.isDark
              ? 'radial-gradient(ellipse 60% 46% at 18% 8%, rgba(13,92,71,0.20), transparent 68%), radial-gradient(ellipse 46% 40% at 88% 30%, rgba(23,138,104,0.10), transparent 70%)'
              : 'radial-gradient(ellipse 60% 46% at 18% 8%, rgba(13,92,71,0.11), transparent 68%), radial-gradient(ellipse 46% 40% at 88% 30%, rgba(23,138,104,0.06), transparent 70%)',
          }}
        />
      ) : null}

      <View
        style={{
          width: '100%',
          maxWidth: LAYOUT.max,
          paddingHorizontal: pad,
          paddingTop: (isXS ? topInset : 0) + fluid(width, 100, 150),
        }}
      >
        <View
          style={isWeb ? {
            opacity: entered ? 1 : 0,
            transform: [{ translateY: entered ? 0 : 12 }],
            transition: `opacity 700ms ${EASE}, transform 700ms ${EASE}`,
          } : null}
        >
          <Kicker>Enterprise · Custom AI engagements</Kicker>
        </View>

        <DisplayHeading
          tag="h1"
          entered={entered}
          type={headline}
          delay={120}
          style={{ marginTop: fluid(width, 26, 40) }}
          lines={[
            ['AI built for'],
            [
              'your ',
              <Accent key="accent" size={headline.fontSize} leading={headline.lineHeight}>business</Accent>,
              ', not',
            ],
            ['a seat licence.'],
          ]}
        />

        <Rule style={{ marginTop: fluid(width, 32, 56) }} />

        <View
          style={{
            flexDirection: isCompact ? 'column' : 'row',
            gap: isCompact ? 28 : 56,
            alignItems: isCompact ? 'stretch' : 'flex-start',
            paddingTop: fluid(width, 26, 38),
            ...(isWeb ? {
              opacity: entered ? 1 : 0,
              transform: [{ translateY: entered ? 0 : 18 }],
              transition: `opacity 800ms ${EASE} 420ms, transform 800ms ${EASE} 420ms`,
            } : {}),
          }}
        >
          <View style={{ flex: isCompact ? undefined : 1.15, minWidth: 0 }}>
            <Lede max={520}>
              Our engineers design, build, and deploy AI systems for organizations that
              need more than a subscription — custom model work, integration with the
              systems you already run, and deployments that pass your security review.
            </Lede>
          </View>

          <View style={{ flex: isCompact ? undefined : 1, minWidth: 0, gap: 14 }}>
            <CtaRow>
              <PrimaryCta label="Schedule a consultation" href="/contact" full={isXS} />
              <GhostCta
                label={CONTACT_EMAIL}
                icon="envelope"
                onPress={() => Linking.openURL(`mailto:${CONTACT_EMAIL}`)}
              />
            </CtaRow>
            <Text style={{ color: c.faint, fontSize: 12.5, letterSpacing: 0.1 }}>
              Free consultation · Quoted per engagement
            </Text>
          </View>
        </View>

        <View
          style={{
            marginTop: fluid(width, 44, 72),
            gap: 16,
            ...(isWeb ? {
              opacity: entered ? 1 : 0,
              transform: [{ translateY: entered ? 0 : 22 }],
              transition: `opacity 900ms ${EASE} 560ms, transform 900ms ${EASE} 560ms`,
            } : {}),
          }}
        >
          <MicroLabel>What an engagement covers</MicroLabel>
          <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: isXS ? 8 : 10 }}>
            {ENGAGEMENT_SCOPE.map((item) => (
              <View
                key={item.label}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 8,
                  paddingHorizontal: isXS ? 12 : 15,
                  paddingVertical: isXS ? 8 : 10,
                  borderRadius: 999,
                  borderWidth: 1,
                  borderColor: c.rule,
                  backgroundColor: c.panel,
                }}
              >
                <Icon name={item.icon} size={11} color={c.faint} solid />
                <Text weight="medium" style={{ color: c.inkSoft, fontSize: isXS ? 12.5 : 13.5 }}>
                  {item.label}
                </Text>
              </View>
            ))}
          </View>
        </View>
      </View>

      <View style={{ width: '100%', alignSelf: 'stretch', marginTop: fluid(width, 56, 96) }}>
        <BleedRule />
        <Marquee items={HERO_MARQUEE} />
        <BleedRule />
      </View>
    </View>
  );
}

export default React.memo(EnterpriseHero);
